import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-hot-toast';
import { buildJoinUrl, isLocalhostOrigin, resolveInviteOrigin } from '../../utils/joinLink';
import { JOIN_MODE, eventJoinMode } from '../../utils/eventStatus';

/**
 * Invite surface: QR code, copyable link and the event code.
 *
 * Rendered both immediately after party creation and from inside an active
 * party, so a host never has to leave or recreate the event to invite someone.
 */
export default function InvitePanel({ eventCode, eventTitle = '', compact = false, joinMode }) {
  const origin = React.useMemo(() => resolveInviteOrigin(), []);
  const joinUrl = React.useMemo(() => buildJoinUrl(eventCode, origin), [eventCode, origin]);
  const localhostInvite = isLocalhostOrigin(origin);
  const requiresApproval = eventJoinMode({ settings: { joinMode } }) === JOIN_MODE.approval;

  const copy = async (value, message) => {
    try {
      await navigator.clipboard?.writeText(value);
      toast.success(message);
    } catch {
      toast.error('Could not copy. Please copy it manually.');
    }
  };

  if (!eventCode) return null;

  return (
    <div className="space-y-4" data-testid="invite-panel">
      {eventTitle && !compact && (
        <p className="text-sm text-gray-400 text-center">
          {requiresApproval
            ? (
              <>
                Scanning this sends a join request for <span className="text-white">{eventTitle}</span>.
                The host still has to approve.
              </>
            )
            : (
              <>
                Anyone who scans or opens this joins <span className="text-white">{eventTitle}</span>.
              </>
            )}
        </p>
      )}

      <div className="flex justify-center">
        <div className="rounded-xl bg-white p-3">
          <QRCodeSVG
            value={joinUrl}
            size={compact ? 140 : 176}
            level="M"
            aria-label={`QR code to join ${eventTitle || 'the party'}`}
          />
        </div>
      </div>

      {localhostInvite && (
        <p className="text-center text-xs text-amber-400/90" data-testid="invite-localhost-warning">
          This QR points at localhost, so another device cannot open it until the app is reached
          through a LAN or deployed origin.
        </p>
      )}

      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Event code</p>
        <div className="flex items-center gap-2">
          <p className="flex-1 bg-black/40 rounded-lg px-3 py-2 font-mono text-2xl tracking-[0.2em] text-blue-300 text-center">
            {eventCode}
          </p>
          <button
            type="button"
            onClick={() => copy(eventCode, 'Event code copied')}
            className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
          >
            Copy code
          </button>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Join link</p>
        <div className="flex items-center gap-2">
          <p className="flex-1 truncate bg-black/40 rounded-lg px-3 py-2 text-sm text-gray-300">
            {joinUrl}
          </p>
          <button
            type="button"
            onClick={() => copy(joinUrl, 'Join link copied')}
            className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
          >
            Copy link
          </button>
        </div>
      </div>
    </div>
  );
}
